import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import cn from 'classnames';

import { Button, Input } from 'components/atoms';
import { ArrowIcon, CrossIcon } from 'components/icons';
import capitalize from 'utils/capitalize';

import { TranslationType } from 'queries/types/TranslationsResultType';

import './Dictionary.scss';

type DictionaryProps = {
  data: TranslationType | null;
  setData: Dispatch<SetStateAction<TranslationType | null>>;
};

const Dictionary: FC<DictionaryProps> = ({ data, setData }) => {
  const [contextsOrder, setContextsOrder] = useState<string[]>(() =>
    data?.translations ? Object.keys(data.translations) : [],
  );

  const [selectedContext, setSelectedContext] = useState<string>(contextsOrder[0] || '');

  useEffect(() => {
    if (contextsOrder.length === 0 && data?.translations) {
      setContextsOrder(Object.keys(data.translations));
    }
  }, []);

  useEffect(() => {
    if (!contextsOrder.includes(selectedContext)) {
      setSelectedContext(contextsOrder[0] || '');
    }
  }, [contextsOrder, selectedContext]);

  const handleRenameContext = (oldName: string, newName: string) => {
    if (data && !data.translations[newName]) {
      setData((prev) => {
        if (!prev) return null;
        if (!prev.translations[oldName] || oldName === newName) {
          return prev;
        }

        const { [oldName]: oldValue, ...rest } = prev.translations;
        return {
          ...prev,
          translations: {
            ...rest,
            [newName]: oldValue,
          },
        };
      });

      setContextsOrder((prevOrder) => prevOrder.map((key) => (key === oldName ? newName : key)));

      if (selectedContext === oldName) {
        setSelectedContext(newName);
      }
    }
  };

  const handleRemoveContext = (contextName: string) => {
    if (data && data.translations[contextName]) {
      setData((prev) => {
        if (!prev) return null;
        if (!prev.translations[contextName]) return prev;

        // eslint-disable-next-line
        const { [contextName]: _, ...rest } = prev.translations;

        return {
          ...prev,
          translations: rest,
        };
      });

      setContextsOrder((prevOrder) => {
        const newOrder = prevOrder.filter((key) => key !== contextName);

        if (selectedContext === contextName) {
          setSelectedContext(newOrder[0] || '');
        }

        return newOrder;
      });
    }
  };

  const handleAddContext = (newContextName: string) => {
    if (data && !data.translations[newContextName]) {
      setData((prev) => {
        if (!prev) return null;

        if (prev.translations[newContextName]) {
          return prev;
        }

        return {
          ...prev,
          translations: {
            ...prev.translations,
            [newContextName]: [],
          },
        };
      });

      setContextsOrder((prevOrder) => [...prevOrder, newContextName]);

      setSelectedContext(newContextName);
    }
  };

  const handleChangeTranslation = (
    contextKey: string,
    translationIndex: number,
    translationPosition: 0 | 1,
    newValue: string,
  ) => {
    setData((prev) => {
      if (!prev) return null;
      const contextTranslations = prev.translations[contextKey];
      if (!contextTranslations) return prev;

      const newContextTranslations: [string, string][] = contextTranslations.map((tuple, i) => {
        if (i !== translationIndex) return tuple;

        return translationPosition === 0 ? [newValue, tuple[1]] : [tuple[0], newValue];
      });

      return {
        ...prev,
        translations: {
          ...prev.translations,
          [contextKey]: newContextTranslations,
        },
      };
    });
  };

  const handleRemoveTranslation = (contextKey: string, translationIndex: number) => {
    setData((prev) => {
      if (!prev) return null;

      const contextTranslations = prev.translations[contextKey];
      if (!contextTranslations) return prev;

      const newContextTranslations = contextTranslations.filter((_, i) => i !== translationIndex);

      return {
        ...prev,
        translations: {
          ...prev.translations,
          [contextKey]: newContextTranslations,
        },
      };
    });
  };

  const handleAddTranslation = (contextKey: string) => {
    setData((prev) => {
      if (!prev) return null;
      const contextTranslations = prev.translations[contextKey];
      if (!contextTranslations) return prev;

      const newContextTranslations: [string, string][] = [...contextTranslations, ['', '']];

      return {
        ...prev,
        translations: {
          ...prev.translations,
          [contextKey]: newContextTranslations,
        },
      };
    });
  };

  if (!data || (data && !Object.keys(data?.translations).length)) {
    return (
      <div className={'dictionary-empty'}>
        <p>No Translations</p>
      </div>
    );
  }

  return (
    <div className="dictionary">
      <p className="dictionary-title dictionary-title-context">Contexts:</p>
      <p className="dictionary-title">Translations:</p>

      <div className="dictionary__context">
        {contextsOrder.map((item, index) => (
          <div key={index} className={'dictionary__context-box'}>
            <CrossIcon
              className={'dictionary__context-icon'}
              onClick={() => handleRemoveContext(item)}
            />
            <Input
              className={cn(
                'dictionary__context-item',
                selectedContext === item && 'dictionary__context-selected',
              )}
              value={capitalize(item)}
              onClick={() => setSelectedContext(item)}
              onChange={({ target }) => handleRenameContext(item, target.value.toLowerCase())}
            />
          </div>
        ))}
        <Button
          className={'dictionary__translations-add'}
          onClick={() => handleAddContext('New Context')}
        >
          +
        </Button>
      </div>

      <div className="translation__separator" />

      <div className="dictionary__translations">
        {data.translations[selectedContext]?.map((elem, indexI) => (
          <div className="dictionary__translations-item" key={indexI}>
            <Input
              value={elem[0]}
              onChange={({ target }) =>
                handleChangeTranslation(selectedContext, indexI, 0, target.value)
              }
            />
            <div
              className={'dictionary__translations-icon-box'}
              onClick={() => handleRemoveTranslation(selectedContext, indexI)}
            >
              <ArrowIcon
                className={'dictionary__translations-icon dictionary__translations-iconArrow'}
              />
              <CrossIcon
                className={'dictionary__translations-icon dictionary__translations-iconCross'}
              />
            </div>
            <Input
              value={elem[1]}
              onChange={({ target }) =>
                handleChangeTranslation(selectedContext, indexI, 1, target.value)
              }
            />
          </div>
        ))}
        <Button
          className={'dictionary__translations-add'}
          onClick={() => handleAddTranslation(selectedContext)}
        >
          +
        </Button>
        {!data.translations[selectedContext].length ? (
          <p className={'dictionary__translations-empty'}>No Translations</p>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default Dictionary;
